import { ClipboardModule } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  ToolkitAction,
  ToolkitFeatureFlagChange,
  ToolkitModalInput,
  ToolkitPersistedState,
  ToolkitProfile,
  ToolkitRole,
  ToolkitSavePayload
} from './models.toolkit-modal';

interface ToolkitEntry {
  key: string;
  value: string;
}

@Component({
  selector: 'canvas-toolkit-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ClipboardModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule
  ],
  templateUrl: './canvas-toolkit-modal.component.html',
  styleUrls: ['./canvas-toolkit-modal.component.scss']
})
export class CanvasToolkitModalComponent implements OnChanges {
  @Input() config: ToolkitModalInput = {};
  @Input() stateStorageKey: string | null = null;
  @Input() extraActions: ToolkitAction[] = [];

  @Output() saveRequested = new EventEmitter<ToolkitSavePayload>();
  @Output() openExternalRequested = new EventEmitter<string>();
  @Output() jsonRequested = new EventEmitter<unknown>();
  @Output() selectedRolesChange = new EventEmitter<ToolkitRole[]>();
  @Output() expirationUnixChange = new EventEmitter<number | null>();
  @Output() tokenSecretChange = new EventEmitter<string>();
  @Output() actionRequested = new EventEmitter<string>();
  @Output() fastLoginRequested = new EventEmitter<ToolkitProfile>();
  @Output() featureFlagChange = new EventEmitter<ToolkitFeatureFlagChange>();

  selectedRoles: ToolkitRole[] = [];
  expirationUnix: number | null = null;
  tokenSecret = '';
  environmentFilter = '';
  profileFilter = '';
  selectedProfileId: string | number | null = null;
  showTokenSecret = false;
  showTokenPayload = false;
  loginPending = false;

  private persistedStateLoaded = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      this.syncFromConfig();
    }
    if (!this.persistedStateLoaded && this.stateStorageKey) {
      this.applyPersistedState();
      this.persistedStateLoaded = true;
    }
  }

  get loading(): boolean { return !!this.config.loading; }
  get title(): string { return this.config.title ?? 'App ToolKit'; }
  get loadingLabel(): string { return this.config.loadingLabel ?? 'Loading...'; }
  get saveButtonLabel(): string { return this.config.saveButtonLabel ?? 'Save session'; }
  get hasUserSection(): boolean { return !!this.config.user; }
  get hasEnvironmentSection(): boolean { return this.environmentEntries.length > 0; }
  get hasDiagnosticsSection(): boolean { return this.diagnosticEntries.length > 0; }
  get fastLoginEnabled(): boolean {
    return this.config.developmentMode === true && this.config.fastLogin?.enabled === true;
  }
  get environmentEntries(): ToolkitEntry[] { return this.toEntries(this.config.environment); }
  get diagnosticEntries(): ToolkitEntry[] { return this.toEntries(this.config.diagnostics); }
  get filteredEnvironmentEntries(): ToolkitEntry[] {
    const query = this.environmentFilter.trim().toLowerCase();
    return query
      ? this.environmentEntries.filter((entry) => `${entry.key} ${entry.value}`.toLowerCase().includes(query))
      : this.environmentEntries;
  }
  get filteredProfiles(): ToolkitProfile[] {
    const query = this.profileFilter.trim().toLowerCase();
    const profiles = this.config.fastLogin?.profiles ?? [];
    return query
      ? profiles.filter((profile) => `${this.profileLabel(profile)} ${profile.email ?? ''} ${this.profileRoleNames(profile)}`.toLowerCase().includes(query))
      : profiles;
  }
  get selectedProfile(): ToolkitProfile | undefined {
    return (this.config.fastLogin?.profiles ?? []).find((profile) => profile.id === this.selectedProfileId);
  }
  get expireDateLabel(): string {
    return this.expirationUnix ? new Date(this.expirationUnix * 1000).toLocaleString() : '-';
  }
  get tokenPayload(): string {
    const token = this.config.user?.token;
    if (!token) return '';
    try {
      const part = token.split('.')[1];
      const normalized = part.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = decodeURIComponent(atob(normalized).split('').map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''));
      return JSON.stringify(JSON.parse(decoded), null, 2);
    } catch {
      return 'Token payload could not be decoded.';
    }
  }
  get tokenStatus(): string {
    const exp = this.readTokenExpiration();
    if (!exp) return 'Expiration unavailable';
    const remaining = exp * 1000 - Date.now();
    if (remaining <= 0) return 'Expired';
    const minutes = Math.ceil(remaining / 60000);
    return minutes >= 1440 ? `Expires in ${Math.ceil(minutes / 1440)}d` : `Expires in ${minutes}m`;
  }
  get saveDisabled(): boolean { return !this.tokenSecret.trim(); }
  get fastLoginDisabled(): boolean {
    return !this.selectedProfile
      || this.selectedProfile.disabled === true
      || this.config.fastLogin?.loading === true
      || this.loginPending
      || (this.config.fastLogin?.requiresTokenSecret === true && !this.tokenSecret.trim());
  }

  onSelectedRolesChange(roles: ToolkitRole[]): void {
    this.selectedRoles = roles;
    this.selectedRolesChange.emit(roles);
  }
  onExpirationChange(expirationUnix: number | null): void {
    this.expirationUnix = expirationUnix;
    this.expirationUnixChange.emit(expirationUnix);
    this.persistState();
  }
  onTokenSecretChange(tokenSecret: string): void {
    this.tokenSecret = tokenSecret;
    this.tokenSecretChange.emit(tokenSecret);
    this.persistState();
  }
  onProfileChange(profileId: string | number | null): void {
    this.selectedProfileId = profileId;
    this.persistState();
  }
  onFastLogin(): void {
    const profile = this.selectedProfile;
    if (!this.fastLoginEnabled || !profile || profile.disabled) return;
    const requireConfirmation = this.config.fastLogin?.requireConfirmation !== false;
    if (requireConfirmation && !window.confirm(`Switch the development session to ${this.profileLabel(profile)}?`)) return;
    this.loginPending = true;
    this.fastLoginRequested.emit(profile);
  }
  resetLoginPending(): void { this.loginPending = false; }
  onOpenExplorer(): void {
    if (this.config.explorerUrl) this.openExternalRequested.emit(this.config.explorerUrl);
  }
  onSave(): void {
    this.saveRequested.emit({ selectedRoles: this.selectedRoles, expirationUnix: this.expirationUnix, tokenSecret: this.tokenSecret });
  }
  onPresetExpiration(hours: number): void {
    this.onExpirationChange(Math.round(Date.now() / 1000) + hours * 3600);
  }
  onAction(actionId: string): void { this.actionRequested.emit(actionId); }
  onFeatureFlagChange(flagIndex: number, enabled: boolean): void {
    const flag = this.config.featureFlags?.[flagIndex];
    if (flag) this.featureFlagChange.emit({ flag, enabled });
  }
  profileLabel(profile: ToolkitProfile): string {
    return profile.label ?? ([profile.firstname, profile.lastname].filter(Boolean).join(' ') || String(profile.email ?? profile.id));
  }
  profileRoleNames(profile: ToolkitProfile): string {
    return (profile.roles ?? []).map((role) => role.name).join(', ');
  }
  compareRoles = (a: ToolkitRole, b: ToolkitRole) => a && b
    ? (a.id !== undefined && b.id !== undefined ? a.id === b.id : a.name === b.name)
    : a === b;

  private syncFromConfig(): void {
    this.selectedRoles = [...(this.config.selectedRoles ?? this.config.user?.roles ?? [])];
    this.expirationUnix = this.config.expirationUnix ?? null;
    this.tokenSecret = this.config.tokenSecret ?? '';
  }
  private toEntries(source?: Record<string, unknown>): ToolkitEntry[] {
    if (!source) return [];
    return Object.keys(source).sort((a, b) => a.localeCompare(b)).map((key) => ({ key, value: this.stringify(source[key]) }));
  }
  private stringify(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    try { return JSON.stringify(value); } catch { return String(value); }
  }
  private readTokenExpiration(): number | null {
    try {
      const parsed = JSON.parse(this.tokenPayload) as { exp?: number };
      return typeof parsed.exp === 'number' ? parsed.exp : null;
    } catch { return null; }
  }
  private applyPersistedState(): void {
    if (!this.stateStorageKey) return;
    try {
      const raw = localStorage.getItem(this.stateStorageKey);
      if (!raw) return;
      const state = JSON.parse(raw) as ToolkitPersistedState;
      if (state.tokenSecret !== undefined) this.tokenSecret = state.tokenSecret;
      if (state.expirationUnix !== undefined) this.expirationUnix = state.expirationUnix;
      if (state.selectedProfileId !== undefined) this.selectedProfileId = state.selectedProfileId;
    } catch {
      localStorage.removeItem(this.stateStorageKey);
    }
  }
  private persistState(): void {
    if (!this.stateStorageKey) return;
    const state: ToolkitPersistedState = {
      tokenSecret: this.tokenSecret,
      expirationUnix: this.expirationUnix,
      selectedProfileId: this.selectedProfileId
    };
    localStorage.setItem(this.stateStorageKey, JSON.stringify(state));
  }
}
